import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types';
import {
  loginRequest,
  registerRequest,
  logoutRequest,
  refreshSession,
  changePasswordRequest,
  BackendUser,
} from '../services/authApi';
import { getMyCustomerProfile, Customer } from '../services/customersApi';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  hydrated: boolean;
  // True when the backend flagged this account for a forced password reset
  // (admin-onboarded customers start on a shared temporary password). The UI
  // gates the storefront behind ForcePasswordModal until it's cleared.
  mustChangePassword: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  hydrate: () => Promise<void>;
}

/**
 * Convert a backend `Customer` profile (with full customer_profile fields)
 * into the frontend `User` shape that all existing components consume.
 */
function customerToUser(c: Customer): User {
  const [firstName, ...rest] = (c.full_name || '').trim().split(/\s+/);
  return {
    id: String(c.id),
    email: c.email,
    firstName: firstName || c.email,
    lastName: rest.join(' ') || '',
    phone: c.contact_phone ?? undefined,
    address: c.address_line1
      ? {
          street: c.address_line1,
          city: c.city ?? '',
          state: c.state ?? '',
          zipCode: c.zip_code ?? '',
          country: 'US',
        }
      : undefined,
  };
}

/**
 * Convert a backend `BackendUser` (login/users payload, without
 * customer_profile fields) into a `User`. Used as a fallback when the
 * /customers/me lookup fails (e.g. user is not Customer role, or backend
 * temporarily unavailable).
 */
function backendUserToUser(u: BackendUser): User {
  const [firstName, ...rest] = (u.full_name || '').trim().split(/\s+/);
  return {
    id: String(u.id),
    email: u.email,
    firstName: firstName || u.email,
    lastName: rest.join(' ') || '',
    // Forward admin-configured `users.phone` (migration 027) so the value
    // shows up on the Profile page. customerToUser still wins when a
    // customer_profiles.contact_phone is available — that's the canonical
    // contact number for customers — but this is the fallback.
    phone: u.phone ?? undefined,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      hydrated: false,
      mustChangePassword: false,

      login: async (email: string, password: string) => {
        const res = await loginRequest(email, password);
        // Try to enrich with the customer profile (address, phone, etc.).
        // Falls back to the lean BackendUser if the role is not Customer.
        let user: User;
        try {
          const profile = await getMyCustomerProfile();
          user = customerToUser(profile);
        } catch {
          user = backendUserToUser(res.user);
        }
        set({
          user,
          isAuthenticated: true,
          hydrated: true,
          mustChangePassword: res.user.must_change_password === true,
        });
      },

      logout: () => {
        logoutRequest();
        set({ user: null, isAuthenticated: false, mustChangePassword: false });
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        await changePasswordRequest(currentPassword, newPassword);
        // Backend cleared `must_change_password`; drop the gate so the
        // storefront becomes usable without needing a re-login.
        set({ mustChangePassword: false });
      },

      register: async (email, password, firstName, lastName) => {
        const full_name = `${firstName} ${lastName}`.trim() || email;
        await registerRequest({ email, password, full_name, role: 'Customer' });
        // Auto-login after successful registration so the modal's existing
        // close-and-continue UX works unchanged.
        await get().login(email, password);
      },

      hydrate: async () => {
        // Called once on app boot to restore session from a stored token.
        const session = await refreshSession();
        if (session) {
          // /users/me returns the live forced-reset flag, so a reload keeps
          // enforcing it until the customer actually changes their password.
          const mustChangePassword = session.user.must_change_password === true;
          try {
            const profile = await getMyCustomerProfile();
            set({ user: customerToUser(profile), isAuthenticated: true, hydrated: true, mustChangePassword });
          } catch {
            set({
              user: backendUserToUser(session.user),
              isAuthenticated: true,
              hydrated: true,
              mustChangePassword,
            });
          }
        } else {
          set({ user: null, isAuthenticated: false, hydrated: true, mustChangePassword: false });
        }
      },
    }),
    {
      name: 'vinpulse-ecom-auth',
      // Persist the user object so the UI can render immediately on reload;
      // `hydrate()` then revalidates against the backend.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        mustChangePassword: state.mustChangePassword,
      }),
    }
  )
);
